import React, { useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button, Checkbox, FormControlLabel, CircularProgress, Box, Alert, Typography } from '@mui/material';
import PrivacyPolicy from '../../PrivacyPolicy'; // Assuming this is the path to your PrivacyPolicy component content

const PrivacyPolicyDialog = ({
  open,
  onAccept, 
}) => {
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(''); // For errors during submission

  const handleSubmitConsent = async () => {
    if (!consentChecked) {
        setError("Please check the box to agree to the privacy policy and give your consent.");
        return;
    }
    setError(''); // Clear previous errors
    setIsSubmitting(true);
    try {
      await onAccept(); 
    } catch (err) {
      console.error("Error accepting privacy policy:", err);
      setError(err.message || "An unexpected error occurred while saving your consent. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog 
        open={open} 
        onClose={(event, reason) => {
            if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                // If you want to allow closing and treat it as decline:
                // onAccept(false); 
            }
        }}
        disableEscapeKeyDown
        maxWidth="md"
        fullWidth
        // Ensure dialog paper has a background that contrasts with elements if needed
        // PaperProps={{ sx: { bgcolor: 'background.paper' } }} // Usually default and fine
    >
      <DialogTitle sx={{ 
        bgcolor: 'primary.main', 
        color: 'primary.contrastText',
        // Ensure primary.main and primary.contrastText are well-defined in your theme for both modes
      }}>
        Privacy Policy and Consent
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ maxHeight: '60vh', overflowY: 'auto', p: 1 }}> 
          <PrivacyPolicy />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, flexDirection: 'column', alignItems: 'stretch' }}>
        {error && <Alert severity="error" sx={{ mb: 2, width: '100%' }}>{error}</Alert>}
        <FormControlLabel
          control={
            <Checkbox
              checked={consentChecked}
              onChange={(e) => {
                setConsentChecked(e.target.checked);
                if (e.target.checked) setError(''); // Clear error when checkbox is checked
              }}
              name="consent"
              color="primary" // Uses theme's primary color for the checkmark
              sx={{
                // Ensure checkbox color is visible in light mode
                // For unchecked state, border should be visible.
                // For checked state, primary color is used.
                // If primary color is too light for light mode, this is a theme issue.
                // Example explicit styling if theme's primary is problematic in light mode:
                // color: (theme) => theme.palette.mode === 'light' ? theme.palette.info.dark : theme.palette.primary.main,
                // '&.Mui-checked': {
                //   color: (theme) => theme.palette.mode === 'light' ? theme.palette.info.dark : theme.palette.primary.main,
                // },
              }}
            />
          }
          label="I have read and agree to the privacy policy and give my consent for data processing."
          sx={{ 
            mb: 2, 
            // Ensure label text is visible in light mode
            color: (theme) => error && !consentChecked 
              ? theme.palette.error.main 
              : (theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.87)' : theme.palette.text.primary),
            // The 'rgba(0, 0, 0, 0.87)' is a common dark text color for light backgrounds.
            // theme.palette.text.primary should ideally handle this.
          }}
        />
        <Button
          onClick={handleSubmitConsent}
          color="primary" // Uses theme's primary color
          variant="contained" // Background is primary.main, text is primary.contrastText
          disabled={isSubmitting}
          fullWidth
          sx={{
            // Ensure button is visible in light mode.
            // If theme.palette.primary.main is too light for a light background,
            // and theme.palette.primary.contrastText is also light, it will be invisible.
            // Example explicit styling if theme's primary is problematic in light mode:
            // bgcolor: (theme) => theme.palette.mode === 'light' ? theme.palette.info.dark : theme.palette.primary.main,
            // color: (theme) => theme.palette.mode === 'light' ? theme.palette.common.white : theme.palette.primary.contrastText,
            // '&:hover': {
            //   bgcolor: (theme) => theme.palette.mode === 'light' ? theme.palette.info.main : theme.palette.primary.dark,
            // }
          }}
        >
          {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Accept and Continue"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrivacyPolicyDialog;
